/**
 * Stands, and what may go on them.
 *
 * The register is the easy half. The half that matters is the four ways an
 * allocation can be wrong, each of which an allocator has to be able to
 * explain rather than merely refuse:
 *
 *  1. **Two aircraft at once.** The commonest, and the one a spreadsheet
 *     misses when a turnaround runs long.
 *  2. **Too big for the stand.** A code-E widebody on a code-C stand is not a
 *     tight fit, it is a wingtip through a jet bridge.
 *  3. **The neighbour.** Some stands cannot be occupied at the same time as
 *     the one beside them — the platform's own schema carries
 *     `excludes_stand` and calls it "the commonest reason a spare stand turns
 *     out not to be spare". A register that stores the stand and forgets the
 *     exclusion will confidently offer a stand that is not there.
 *  4. **The stand is not available.** Closed for work, or closed by a closure
 *     of the apron it sits on. This is the one that connects the flight layer
 *     to the rest of the platform: a pavement defect raised as a work order
 *     against APRON-1 takes Stand 40 out of the allocation, and nobody has to
 *     tell the flight layer separately.
 *
 * Every refusal names which of the four it is and what it collided with. An
 * allocator that says "no" without saying "against what" gets overridden
 * within a week and then ignored.
 *
 * Planned and actual are the same shape with a flag, as the platform stores
 * them, so a query that forgets to filter gets a visibly wrong answer rather
 * than a quietly plausible one.
 */
import { t, tn } from "../i18n";
import { bestKnown, fitsIn, flightName, SIZE_ORDER, type Flight, type SizeCode } from "./flights";
import { hhmm } from "../lib/format";

export type StandKind = "contact" | "remote" | "cargo" | "ga" | "maintenance" | "de_icing";

export const STAND_KIND_LABEL: Record<StandKind, string> = {
  contact: "Contact stand",
  remote: "Remote stand",
  cargo: "Cargo stand",
  ga: "General aviation",
  maintenance: "Maintenance",
  de_icing: "De-icing",
};

export interface Stand {
  code: string;
  name?: string;
  kind: StandKind;
  /** the largest code letter this stand takes */
  maxSize: SizeCode;
  /** the apron or zone it sits on — how a closure or a work order reaches it */
  zoneCode?: string;
  /** stands that cannot be occupied at the same time as this one */
  excludes?: string[];
  /** a jet bridge, where there is one */
  bridge?: boolean;
  /**
   * The gate passengers walk to, which is not the stand the aircraft is on.
   * A remote stand has none, and a display that printed a stand number for
   * one would send people to look for a door that does not exist.
   */
  gate?: string;
  /** taken out of use by the airport itself, with a reason */
  outOfService?: { from?: string; to?: string; why: string; by?: string };
}

export interface StandAssignment {
  id: string;
  standCode: string;
  flightId: string;
  from: string;
  to: string;
  /** false while it is a plan; true once the aircraft is actually there */
  actual: boolean;
  by?: string;
  note?: string;
}

export interface StandRegister {
  stands: Stand[];
  assignments: StandAssignment[];
  enteredBy?: string;
  enteredAt?: string;
}

const ms = (iso: string) => Date.parse(iso);
const overlaps = (a: { from: string; to: string }, b: { from: string; to: string }) =>
  ms(a.from) < ms(b.to) && ms(b.from) < ms(a.to);

/* ── what makes a stand unavailable ───────────────────────────────────── */

/**
 * Something outside the flight layer that closes a stand.
 *
 * Kept as a plain shape rather than a reference to the work-order or closure
 * types, so the flight layer depends on neither. The screen that has both
 * builds these and hands them in — which is also what makes the connection
 * testable without a platform.
 */
export interface StandBlock {
  standCode: string;
  from?: string;
  to?: string;
  /** "work order", "closure", "out of service" */
  kind: string;
  why: string;
  /** the work order number or closure reference, so somebody can go and look */
  ref?: string;
  /**
   * Whether this actually closes the stand, or is work an allocator should
   * know about.
   *
   * The distinction matters more than it looks. A marking repaint on one
   * stand does not close the apron it sits on — and a rule that treated every
   * work order's zone as a closure shut thirteen of fifteen stands at OEMA on
   * a day with two jobs open. What closes a surface is a CLOSURE; a work
   * order closes a stand only when it names that stand.
   */
  severity?: "closed" | "caution";
}

/** Blocks that bite during a window. A block with no dates bites always. */
export function blocksDuring(blocks: readonly StandBlock[], standCode: string, window: { from: string; to: string }): StandBlock[] {
  return blocks.filter((b) => {
    if (b.standCode !== standCode) return false;
    if (!b.from && !b.to) return true;
    return overlaps({ from: b.from ?? window.from, to: b.to ?? window.to }, window);
  });
}

/* ── the four ways an allocation can be wrong ─────────────────────────── */

export type ConflictKind = "occupied" | "too_big" | "neighbour" | "blocked";

export interface Conflict {
  kind: ConflictKind;
  /** said in full, naming what it collided with */
  why: string;
  /** the other flight, where one is involved */
  otherFlightId?: string;
  /** the other stand, for an exclusion */
  otherStand?: string;
  ref?: string;
}

export interface CheckInput {
  register: StandRegister;
  flights: readonly Flight[];
  blocks: readonly StandBlock[];
  /** the assignment being tested; its own id is ignored when it already exists */
  standCode: string;
  flight: Flight;
  window: { from: string; to: string };
  ignoreAssignmentId?: string;
}

/**
 * Everything wrong with putting this flight on this stand, in this window.
 *
 * Returns them all rather than the first: a stand that is both too small and
 * already occupied has two problems, and an allocator that reports one sends
 * somebody back twice.
 */
export function conflictsFor(inp: CheckInput): Conflict[] {
  const { register, flights, blocks, standCode, flight, window } = inp;
  const stand = register.stands.find((s) => s.code === standCode);
  const out: Conflict[] = [];
  if (!stand) {
    return [{ kind: "blocked", why: t("{stand} is not a stand on this airport.", { stand: standCode }) }];
  }

  /* 1 — something else is on it */
  for (const a of register.assignments) {
    if (a.standCode !== standCode) continue;
    if (a.id === inp.ignoreAssignmentId) continue;
    if (a.flightId === flight.id) continue;
    if (!overlaps(a, window)) continue;
    const other = flights.find((f) => f.id === a.flightId);
    out.push({
      kind: "occupied",
      otherFlightId: a.flightId,
      why: t("{stand} is taken by {flight} from {from} to {to}.", {
        stand: standCode,
        flight: other ? flightName(other) : t("another movement"),
        /* the aerodrome's clock, the same one the board beside this prints.
           Slicing the ISO string here printed UTC, three hours out. */
        from: hhmm(a.from),
        to: hhmm(a.to),
      }),
    });
  }

  /* 2 — the aircraft is too big for it */
  if (flight.size && !fitsIn(flight.size, stand.maxSize)) {
    out.push({
      kind: "too_big",
      why: t("{stand} takes code {max}; {flight} is code {size}.", {
        stand: standCode,
        max: stand.maxSize,
        flight: flightName(flight),
        size: flight.size,
      }),
    });
  }

  /* 3 — the neighbour it cannot share with */
  for (const excluded of stand.excludes ?? []) {
    for (const a of register.assignments) {
      if (a.standCode !== excluded) continue;
      if (a.id === inp.ignoreAssignmentId) continue;
      if (!overlaps(a, window)) continue;
      const other = flights.find((f) => f.id === a.flightId);
      out.push({
        kind: "neighbour",
        otherStand: excluded,
        otherFlightId: a.flightId,
        why: t("{stand} cannot be used while {other} is occupied, and {flight} is on it.", {
          stand: standCode,
          other: excluded,
          flight: other ? flightName(other) : t("another movement"),
        }),
      });
    }
  }

  /* 4 — it is closed, by this layer or by the rest of the platform */
  if (stand.outOfService) {
    out.push({
      kind: "blocked",
      why: t("{stand} is out of service: {why}", { stand: standCode, why: stand.outOfService.why }),
    });
  }
  for (const b of blocksDuring(blocks, standCode, window)) {
    if (b.severity === "caution") continue;
    out.push({
      kind: "blocked",
      ref: b.ref,
      why: t("{stand} is closed by {kind} {ref}: {why}", {
        stand: standCode,
        kind: t(b.kind),
        ref: b.ref ?? "",
        why: b.why,
      }),
    });
  }

  return out;
}

/* ── what to put it on instead ────────────────────────────────────────── */

export interface Candidate {
  stand: Stand;
  conflicts: Conflict[];
  /** free, and nothing against it */
  clear: boolean;
  /** why this one rather than another, when it is clear */
  because: string;
}

/**
 * Where this flight could go, best first.
 *
 * "Best" is not cleverness, it is a stated preference order, and the reason
 * is returned with each candidate so the person choosing can disagree with
 * it:
 *
 *   a contact stand before a remote one, because passengers walk;
 *   the SMALLEST stand the aircraft fits on, because putting a code-C on a
 *   code-E stand spends the only stand a widebody can use;
 *   then whatever is left, in code order, so the list is stable.
 *
 * Stands with conflicts are still returned, last, with their conflicts —
 * because "everything is full" is an answer somebody needs to see the shape
 * of, not a blank list.
 */
export function candidatesFor(inp: Omit<CheckInput, "standCode">): Candidate[] {
  const scored = inp.register.stands.map((stand) => {
    const conflicts = conflictsFor({ ...inp, standCode: stand.code });
    const clear = conflicts.length === 0;
    const size = inp.flight.size;
    const slack = size ? SIZE_ORDER.indexOf(stand.maxSize) - SIZE_ORDER.indexOf(size) : 0;
    const because = !clear
      ? ""
      : stand.kind === "contact"
        ? slack === 0
          ? t("a contact stand, and exactly the size this aircraft needs")
          : t("a contact stand; {n} larger than this aircraft needs", { n: tn(slack, "{n} size", "{n} sizes") })
        : t("{kind}; passengers are bussed", { kind: t(STAND_KIND_LABEL[stand.kind]) });
    return { stand, conflicts, clear, because, slack, contact: stand.kind === "contact" };
  });
  scored.sort((a, b) => {
    if (a.clear !== b.clear) return a.clear ? -1 : 1;
    if (a.clear) {
      if (a.contact !== b.contact) return a.contact ? -1 : 1;
      if (a.slack !== b.slack) return a.slack - b.slack;
    }
    return a.stand.code.localeCompare(b.stand.code);
  });
  return scored.map(({ stand, conflicts, clear, because }) => ({ stand, conflicts, clear, because }));
}

/* ── reading the board ────────────────────────────────────────────────── */

export interface StandRow {
  stand: Stand;
  /** assignments on this stand in the window asked for, in time order */
  assignments: (StandAssignment & { flight?: Flight })[];
  /** conflicts found among what is ALREADY assigned, not a proposal */
  conflicts: Conflict[];
  blocked: StandBlock[];
  /** work on this stand's surface that does not close it, but is worth knowing */
  cautions: StandBlock[];
  /** minutes of the window it is occupied for */
  occupiedMinutes: number;
}

/**
 * The board: every stand, what is on it, and what is wrong with what is on it.
 *
 * The conflicts here are in the PLAN as it stands. An allocation board that
 * only checks a new assignment cannot tell you that two things you approved
 * last week now overlap because one of them moved.
 */
export function standBoard(
  register: StandRegister,
  flights: readonly Flight[],
  blocks: readonly StandBlock[],
  window: { from: string; to: string },
): StandRow[] {
  const byFlight = new Map(flights.map((f) => [f.id, f]));
  return register.stands.map((stand) => {
    const assignments = register.assignments
      .filter((a) => a.standCode === stand.code && overlaps(a, window))
      .sort((a, b) => a.from.localeCompare(b.from))
      .map((a) => ({ ...a, flight: byFlight.get(a.flightId) }));

    const conflicts: Conflict[] = [];
    for (const a of assignments) {
      const flight = byFlight.get(a.flightId);
      if (!flight) continue;
      for (const c of conflictsFor({
        register,
        flights,
        blocks,
        standCode: stand.code,
        flight,
        window: { from: a.from, to: a.to },
        ignoreAssignmentId: a.id,
      })) {
        /* the same overlap is found from both sides; keep one */
        if (!conflicts.some((x) => x.kind === c.kind && x.why === c.why)) conflicts.push(c);
      }
    }

    const occupiedMinutes = assignments.reduce(
      (n, a) => n + Math.max(0, (Math.min(ms(a.to), ms(window.to)) - Math.max(ms(a.from), ms(window.from))) / 60_000),
      0,
    );

    const touching = blocksDuring(blocks, stand.code, window);
    return {
      stand,
      assignments,
      conflicts,
      blocked: touching.filter((b) => b.severity !== "caution"),
      cautions: touching.filter((b) => b.severity === "caution"),
      occupiedMinutes,
    };
  });
}

/** A flight with no stand in the window: the list an allocator works from. */
export function unallocated(
  register: StandRegister,
  flights: readonly Flight[],
  window: { from: string; to: string },
): Flight[] {
  const placed = new Set(register.assignments.filter((a) => overlaps(a, window)).map((a) => a.flightId));
  return flights
    .filter((f) => !f.cancelled && f.source !== "forecast" && !placed.has(f.id))
    .filter((f) => {
      const at = bestKnown(f).at;
      return at !== null && ms(at) >= ms(window.from) && ms(at) <= ms(window.to);
    })
    .sort((a, b) => (bestKnown(a).at ?? "").localeCompare(bestKnown(b).at ?? ""));
}

/** How full the estate is, over a window. */
export interface StandUse {
  stands: number;
  usable: number;
  blocked: number;
  occupiedNow: number;
  /** the share of the window's stand-minutes actually used */
  utilisationPct: number | null;
  conflicts: number;
  unallocated: number;
}

export function standUse(rows: readonly StandRow[], window: { from: string; to: string }, unplaced: number): StandUse {
  const minutes = Math.max(1, (ms(window.to) - ms(window.from)) / 60_000);
  const usable = rows.filter((r) => r.blocked.length === 0 && !r.stand.outOfService);
  const used = usable.reduce((n, r) => n + r.occupiedMinutes, 0);
  return {
    stands: rows.length,
    usable: usable.length,
    blocked: rows.length - usable.length,
    occupiedNow: rows.filter((r) => r.assignments.length > 0).length,
    utilisationPct: usable.length ? Math.round((used / (usable.length * minutes)) * 100) : null,
    conflicts: rows.reduce((n, r) => n + r.conflicts.length, 0),
    unallocated: unplaced,
  };
}
